import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type QueryClient } from "@tanstack/react-query";
import { z } from "zod";

export const WORKER_TRIPS_PATH = "/worker/:workerId/trips";

const latestWorkerMovementSchema = z.enum(["MOVING", "STATIONARY", "UNKNOWN"]);
const tripStatusSchema = z.enum([
  "TRIP_STARTED",
  "TRIP_PENDING",
  "TRIP_APPROACHING",
  "TRIP_ARRIVED",
  "TRIP_COMPLETED",
  "TRIP_CANCELLED",
  "TRIP_EXPIRED",
  "TRIP_PLACEHOLDER",
]);
const tripProviderSchema = z.enum(["Radar", "HyperTrack"]);

const workerTripAttributesSchema = z.object({
  _id: z.string(),
  workerId: z.string(),
  shiftId: z.string(),
  workplaceId: z.string(),
  locationProviderTripId: z.string().optional(),
  currentEtaDuration: z.number(),
  currentEtaDistance: z.number(),
  initialEtaDuration: z.number(),
  initialEtaDistance: z.number(),
  latestWorkerMovement: latestWorkerMovementSchema.optional(),
  latestWorkerMovementTransitionedAt: z.string().datetime().optional(),
  currentLatitude: z.number().optional(),
  currentLongitude: z.number().optional(),
  status: tripStatusSchema,
  hasWorkerApproachingNotificationBeenSent: z.boolean(),
  hasWorkerRunningLateNotificationBeenSent: z.boolean(),
  tripStartTime: z.string().datetime(),
  tripStopTime: z.string().datetime().optional(),
  workerDeclaredEta: z.string().datetime().optional(),
  providerName: tripProviderSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastLocationReceivedByServerAt: z.string().datetime().optional(),
  isLate: z.boolean(),
});

const workerTripResourceSchema = z.object({
  type: z.literal("trip"),
  id: z.string(),
  attributes: workerTripAttributesSchema,
});

const workerTripsResponseSchema = z.array(workerTripResourceSchema);

const workerTripsFilterSchema = z.object({
  "shift.id": z.string(),
});

const getWorkerTripsRequestSchema = z.object({
  filter: workerTripsFilterSchema,
});

export type WorkerTripAttributes = z.infer<typeof workerTripAttributesSchema>;
export type WorkerTripResource = z.infer<typeof workerTripResourceSchema>;
export type WorkerTripsResponse = z.infer<typeof workerTripsResponseSchema>;
export type WorkerTripsFilter = z.infer<typeof workerTripsFilterSchema>;
export type GetWorkerTripsRequest = z.infer<typeof getWorkerTripsRequestSchema>;

export interface UseGetWorkerTripsParams {
  shiftId: string;
}

export function buildWorkerTripsUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}${WORKER_TRIPS_PATH.replace(
    ":workerId",
    workerId
  )}`;
}

export function getWorkerTripsQueryKey(params: { workerId: string; shiftId: string }) {
  const { workerId, shiftId } = params;

  const queryParams: GetWorkerTripsRequest = {
    filter: {
      "shift.id": shiftId,
    },
  };

  return [buildWorkerTripsUrl(workerId), queryParams] as const;
}

export async function invalidateWorkerTrips(
  queryClient: QueryClient,
  workerId: string,
  shiftId: string
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: getWorkerTripsQueryKey({ workerId, shiftId }),
    exact: true,
  });
}

export function useGetWorkerTrips(
  params: UseGetWorkerTripsParams,
  options: UseGetQueryOptions<WorkerTripsResponse> = {}
) {
  const worker = useDefinedWorker();
  const { shiftId } = params;

  const [, queryParams] = getWorkerTripsQueryKey({
    workerId: worker.userId,
    shiftId,
  });

  return useGetQuery<GetWorkerTripsRequest, WorkerTripsResponse>({
    url: buildWorkerTripsUrl(worker.userId),
    queryParams,
    requestSchema: getWorkerTripsRequestSchema,
    responseSchema: workerTripsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_TRIPS_FAILURE,
    },
    ...options,
  });
}
